import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import { logEvent } from 'config/tracking';
import ActivityType from 'core/enums/activityType';
import EventMediaType from 'core/enums/eventMediaType';
import EventStatus from 'core/enums/eventStatus';
import EventType, {
    EventTypeWithApplication,
    EventTypeWithoutApplication,
    EventTypeWithoutContact,
} from 'core/enums/eventType';
import InterviewType from 'core/enums/interviewType';
import MeetingType from 'core/enums/meetingType';
import { useApplicationById, useApplications } from 'core/api/applications';
import { useContactById, useContacts } from 'core/api/contacts';
import { useCreateEventMutation, useUpdateEventMutation } from 'core/api/events';
import Event from 'core/models/event';
import { getApplicationLabel } from 'core/useCases/applications/utils';
import { getEventMediaOptionsForContact } from 'core/useCases/contacts/utils';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import useResponsive from 'hooks/responsive';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import { SSpacedColumn } from 'styles/layout';
import { enumKeys } from 'utils/common';
import { concatDateTime } from 'utils/dateTimeFormat';
import { TrackingEventName } from 'utils/types/amplitude';
import { DateOrTimeInput, SelectInput } from '../lib';
import ResourcesInput from '../lib/ResourcesInput';
import { OptionsProps } from '../lib/SelectInput';
import TextInput from '../lib/TextInput';

type Inputs = {
    applicationId: string;
    type: EventType;
    subject?: InterviewType | MeetingType;
    contactId?: string;
    mediaType?: EventMediaType;
    mediaDetails?: string;
    date: Date;
    time: Date;
    comments?: string;
};

const computeData = (event?: Partial<Event>): Partial<Inputs> | undefined => {
    if (!event) {
        return undefined;
    }
     
    const { contact, application, id, date, ...data } = event;
    const computedData = {
        ...data,
        applicationId: application?.id,
        contactId: contact?.id,
        date: date ? new Date(date) : undefined,
        time: date ? new Date(date) : undefined,
    } as Partial<Inputs>;

    return computedData;
};

type EventFormProps = {
    closeModal(): void;
    defaultValues: Partial<Event>;
    disabledFields?: Array<DisabledField>;
};

const EventForm: FC<EventFormProps> = ({ closeModal, defaultValues, disabledFields }) => {
    const openModal = useModalStore((s) => s.openModal);
    const createEventMutation = useCreateEventMutation();
    const updateEventMutation = useUpdateEventMutation();
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const initialEventType = defaultValues?.type || EventType.Interview || EventType.Meeting;

    const { data: contactsData } = useContacts();
    const contactList = useMemo(() => contactsData ?? [], [contactsData]);
    const [contactOptions, setContactOptions] = useState<OptionsProps>([]);
    const [subjectOptions, setSubjectOptions] = useState<OptionsProps>([]);
    const [mediaTypeOptions, setMediaTypeOptions] = useState<OptionsProps>([]);
    const { data: applicationsData } = useApplications();
    const applicationOptions = useMemo(
        () =>
            (applicationsData ?? []).map((application) => ({
                label: getApplicationLabel(application, t),
                value: application.id,
            })),
        [applicationsData, t],
    );

    const methods = useForm<Inputs>({
        defaultValues: computeData(defaultValues),
    });
    const { handleSubmit, control, watch, setValue } = methods;
    const selectedContactId = watch('contactId');
    const selectedMediaType = watch('mediaType');
    const selectedSubject = watch('subject');
    const selectedApplicationId = watch('applicationId');
    const selectedContact = useContactById(selectedContactId).data;
    const selectedApplication = useApplicationById(selectedApplicationId).data;

    useEffect(() => {
        if (selectedSubject && EventTypeWithoutContact.includes(selectedSubject as MeetingType)) {
            setValue('type', EventType.Meeting);
        } else {
            setValue('type', EventType.Interview);
        }
    }, [selectedSubject, setValue]);

    const onSubmit = handleSubmit(async (values: Inputs) => {
        let newEvent: Event = {} as Event;
        const eventType = values.type;
        try {
            if (defaultValues?.id) {
                // Remove time from values
                const { date, time, ...data } = values;
                await updateEventMutation.mutateAsync({
                    id: defaultValues.id,
                    data: { ...data, date: concatDateTime(date, time) },
                });
            } else {
                // Remove time from values
                const { date, time, ...data } = values;
                newEvent = await createEventMutation.mutateAsync({
                    ...data,
                    date: concatDateTime(date, time),
                    type: eventType,
                });
                logEvent({
                    name: TrackingEventName.EVENT_CREATED,
                    params: { eventId: newEvent.id, subject: newEvent.subject },
                });
            }
            toast.success(t(`event-form.${defaultValues?.id ? 'update' : 'creation'}.success`));
            closeModal();

            if (newEvent && newEvent.createdDate && newEvent.createdDate > newEvent.date) {
                const { status } = newEvent;
                openModal({
                    defaultValues: {
                        activityType: ActivityType.Event,
                        id: newEvent.id,
                        isAlreadyCompleted: EventStatus.Completed === status,
                    },
                    title: t('common.mark-as-complete'),
                    type: ModalType.ActivityReport,
                });
            }
        } catch {
            toast.error(t(`event-form.${defaultValues?.id ? 'update' : 'creation'}.error`));
        }
    });

    useEffect(() => {
        if (EventType.Interview === initialEventType || EventType.Meeting === initialEventType) {
            const subjectOptionsComputed: OptionsProps = [];
            for (const value of enumKeys(InterviewType)) {
                subjectOptionsComputed.push({
                    label: t(`common.interview-subject.enum.${InterviewType[value]}`),
                    value: InterviewType[value],
                });
            }
            setSubjectOptions(subjectOptionsComputed);
        }
    }, [setSubjectOptions, initialEventType]);

    useEffect(() => {
        if (selectedContact) {
            setMediaTypeOptions(getEventMediaOptionsForContact(selectedContact, t));
        } else if (
            (selectedSubject && selectedSubject === MeetingType.Exhibition) ||
            selectedSubject === MeetingType.Workshop ||
            selectedSubject === MeetingType.Conference ||
            selectedSubject === MeetingType.Training
        ) {
            setMediaTypeOptions([
                { label: t(`common.media-type.enum.face-to-face`), value: EventMediaType.FaceToFace },
                { label: t(`common.media-type.enum.video-conferencing`), value: EventMediaType.VideoConferencing },
            ]);
        } else if (selectedSubject && selectedSubject === MeetingType.Webinar) {
            setMediaTypeOptions([
                { label: t(`common.media-type.enum.video-conferencing`), value: EventMediaType.VideoConferencing },
            ]);
        } else if (
            selectedSubject &&
            (selectedSubject === InterviewType.Coffee ||
                selectedSubject === InterviewType.Meal ||
                selectedSubject === MeetingType.Afterwork)
        ) {
            setMediaTypeOptions([
                { label: t(`common.media-type.enum.face-to-face`), value: EventMediaType.FaceToFace },
            ]);
        }
    }, [setMediaTypeOptions, selectedContact, selectedContactId, selectedSubject, setValue]);

    useEffect(() => {
        // Reset mediaDetails if mediaType not concern with mediaDetails
        if (
            selectedMediaType &&
            ![EventMediaType.FaceToFace, EventMediaType.VideoConferencing].includes(selectedMediaType)
        ) {
            setValue('mediaDetails', undefined);
        }
    }, [selectedMediaType, setValue]);

    // Refresh subject options and contact options according to application selected or not
    useEffect(() => {
        const listOfSubject = selectedApplication ? EventTypeWithApplication : EventTypeWithoutApplication;

        setSubjectOptions(
            listOfSubject.map((subject) => {
                const type = EventTypeWithoutContact.includes(subject as MeetingType)
                    ? EventType.Meeting
                    : EventType.Interview;
                return {
                    label: t(`common.${type}-subject.enum.${subject}`),
                    value: subject,
                };
            }),
        );

        if (selectedSubject && !listOfSubject.includes(selectedSubject)) {
            setValue('subject', undefined);
        }

        const contactsComputed = contactList.filter(
            (contact) => !selectedApplication || selectedApplication.contacts.map((c) => c.id).includes(contact.id),
        );

        const contactIdsComputed = contactsComputed.map((contact) => contact.id);

        setContactOptions(
            contactsComputed.map((contact) => ({
                label: `${contact.firstName} ${contact.lastName}`,
                value: contact.id,
            })),
        );

        // If selected application have changed with a value, reset contact
        // because the contact should be in the list of application linked contact
        if (selectedApplication && selectedContactId && !contactIdsComputed.includes(selectedContactId)) {
            setValue('contactId', undefined);
        }
    }, [setSubjectOptions, selectedApplication, setValue, selectedSubject, selectedContactId, contactList]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SelectInput
                    control={control}
                    isDisabled={disabledFields && disabledFields.includes(DisabledField.ApplicationId)}
                    label={t('application.label')}
                    name="applicationId"
                    options={applicationOptions}
                    semi={!isMobile}
                />
                <SSpacedColumn>
                    <SelectInput
                        control={control}
                        isDisabled={disabledFields && disabledFields.includes(DisabledField.Subject)}
                        label={t('common.subject.label')}
                        name="subject"
                        options={subjectOptions}
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                    />
                    <SelectInput
                        control={control}
                        isDisabled={disabledFields && disabledFields.includes(DisabledField.ContactId)}
                        label={t('common.contact')}
                        name="contactId"
                        options={contactOptions}
                        placeholder={t('common.select-field-placeholder')}
                        required={
                            !(selectedSubject && EventTypeWithoutContact.includes(selectedSubject as MeetingType))
                        }
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <SSpacedColumn>
                    <SelectInput
                        control={control}
                        label={t('common.media-type.label')}
                        name="mediaType"
                        options={mediaTypeOptions}
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                    />
                    { }
                    {(!selectedMediaType ||
                        [EventMediaType.FaceToFace, EventMediaType.VideoConferencing].includes(selectedMediaType)) && (
                        <TextInput
                            label={t('common.media-details.label')}
                            name="mediaDetails"
                            placeholder={t('common.media-details.placeholder')}
                            semi={!isMobile}
                            required
                        />
                    )}
                </SSpacedColumn>
                <SSpacedColumn>
                    <DateOrTimeInput
                        control={control}
                        label={t('common.date')}
                        name="date"
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                    />
                    <DateOrTimeInput
                        control={control}
                        label={t('common.time')}
                        minDate={new Date()}
                        name="time"
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                        typeTime
                    />
                </SSpacedColumn>
                <TextInput
                    label={t('common.comment')}
                    name="comments"
                    placeholder={t('common.write-a-comment')}
                    isTextArea
                />
                <ResourcesInput name="resources" />
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{defaultValues?.id ? t('common.update') : t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default EventForm;
